import React, { useState, useMemo } from 'react';
import { MessageCircle, Mail, Send, ChevronDown, Phone } from 'lucide-react';

/**
 * Auto-outreach button group. Given a lead's contact info + a pre-drafted
 * message, opens the right channel with the text pre-filled:
 *   • WhatsApp → whatsapp:// (native app) or wa.me (web fallback)
 *   • SMS      → sms:?body=
 *   • Email    → mailto:?subject=&body=
 *
 * No backend call needed — everything is URL scheme based.
 */
const AutoOutreachButton = ({ lead, message, subject = 'Following up on your project' }) => {
  const [open, setOpen] = useState(false);

  const clean = (v) => (v || '').toString().trim();
  const digitsOnly = (v) => clean(v).replace(/[^\d+]/g, '');

  const links = useMemo(() => {
    const phone = digitsOnly(lead?.phone || lead?.mobile);
    const email = clean(lead?.email);
    const text = clean(message);
    const encoded = encodeURIComponent(text);
    const encodedSubject = encodeURIComponent(subject);

    return {
      whatsapp: phone ? `https://wa.me/${phone.replace(/^\+/, '')}?text=${encoded}` : null,
      sms: phone ? `sms:${phone}?body=${encoded}` : null,
      email: email ? `mailto:${email}?subject=${encodedSubject}&body=${encoded}` : null,
      tel: phone ? `tel:${phone}` : null,
    };
  }, [lead, message, subject]);

  const anyChannel = links.whatsapp || links.sms || links.email;
  if (!anyChannel || !message) return null;

  const openChannel = (url) => {
    setOpen(false);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="auto-outreach" data-testid="auto-outreach">
      <button
        type="button"
        className="btn btn-primary auto-outreach-trigger"
        onClick={() => setOpen((v) => !v)}
        data-testid="auto-outreach-trigger"
      >
        <Send size={14} />
        Send outreach
        <ChevronDown size={14} className={open ? 'chev open' : 'chev'} />
      </button>
      {open && (
        <div className="auto-outreach-menu" role="menu" data-testid="auto-outreach-menu">
          {links.whatsapp && (
            <button
              type="button"
              onClick={() => openChannel(links.whatsapp)}
              role="menuitem"
              data-testid="auto-outreach-whatsapp"
            >
              <MessageCircle size={14} style={{ color: '#25D366' }} />
              <span className="ch-name">WhatsApp</span>
              <span className="ch-hint">Pre-filled message</span>
            </button>
          )}
          {links.sms && (
            <button
              type="button"
              onClick={() => openChannel(links.sms)}
              role="menuitem"
              data-testid="auto-outreach-sms"
            >
              <MessageCircle size={14} />
              <span className="ch-name">SMS</span>
              <span className="ch-hint">Text with pre-filled body</span>
            </button>
          )}
          {links.email && (
            <button
              type="button"
              onClick={() => openChannel(links.email)}
              role="menuitem"
              data-testid="auto-outreach-email"
            >
              <Mail size={14} />
              <span className="ch-name">Email</span>
              <span className="ch-hint">Opens your mail client</span>
            </button>
          )}
          {links.tel && (
            <button
              type="button"
              onClick={() => openChannel(links.tel)}
              role="menuitem"
              data-testid="auto-outreach-call"
            >
              <Phone size={14} />
              <span className="ch-name">Call</span>
              <span className="ch-hint">Dial from device</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default AutoOutreachButton;
